import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box as MuiBox } from "@mui/material";
import { DataGridPro as MuiDataGrid } from "@mui/x-data-grid-pro";
import { useAlerts } from "common";
import { LoadingGridOverlay } from "common/components/DataGrid/LoadingGridOverlay";
import { NoRowsGridOverlay } from "common/components/DataGrid/NoRowsGridOverlay";
import {
  LOADER_TYPES,
  ProspectOutreachHistoryDefaults,
} from "app/shared/constants";
import { defaultTableColumnHeaders } from "./ProspectDetailsOutreachHistoryConfig";
import { getProspectOutreachHistory } from "app/services/prospectServices";
import { tableStyles } from "app/shared/UI/sharedStyles";

const ProspectDetailsOutreachHistory = ({ mrn }) => {
  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const [outreachHistory, setOutreachHistory] = useState([]);
  const [outreachHistoryFilters, setOutreachHistoryFilters] = useState({
    sort: `${ProspectOutreachHistoryDefaults.OUTREACH_HISTORY_DEFAULT_ORDERBY},${ProspectOutreachHistoryDefaults.OUTREACH_HISTORY_DEFAULT_ORDER}`,
    page: ProspectOutreachHistoryDefaults.OUTREACH_HISTORY_DEFAULT_PAGE,
    size: ProspectOutreachHistoryDefaults.OUTREACH_HISTORY_DEFAULT_PAGESIZE,
  });
  const [sort, setSort] = useState({
    order: ProspectOutreachHistoryDefaults.OUTREACH_HISTORY_DEFAULT_ORDER,
    orderBy: ProspectOutreachHistoryDefaults.OUTREACH_HISTORY_DEFAULT_ORDERBY,
  });
  const [page, setPage] = useState(
    outreachHistoryFilters.page ||
      ProspectOutreachHistoryDefaults.OUTREACH_HISTORY_DEFAULT_PAGE
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    outreachHistoryFilters.size ||
      ProspectOutreachHistoryDefaults.OUTREACH_HISTORY_DEFAULT_PAGESIZE
  );
  const [outreachHistoryTotalCount, setOutreachHistoryTotalCount] = useState(0);

  const initialLoading = {
    prospectDetailsOutreachHistory: true,
  };

  const [isLoading, setIsLoading] = useState(initialLoading);
  // Pseudo-reducer to keep loading states updated
  const setLoader = (type, isSet) => {
    setIsLoading((prev) => ({
      ...prev,
      [type]: isSet,
    }));
  };

  const NoStudyOverlay = () => (
    <NoRowsGridOverlay
      message={!outreachHistory.length && t("common.emptyTableRow")}
    />
  );

  const handleSortChange = (sortModel) => {
    setSort({
      orderBy: sortModel[0].field,
      order: sortModel[0].sort,
    });

    setOutreachHistoryFilters({
      ...outreachHistoryFilters,
      sort: `${sortModel[0].field},${sortModel[0].sort}`,
    });
  };

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
    setOutreachHistoryFilters({
      ...outreachHistoryFilters,
      page: model.page,
      size: model.pageSize,
    });
  };

  useEffect(() => {
    getProspectOutreachHistory(
      mrn,
      setOutreachHistory,
      outreachHistoryFilters,
      setOutreachHistoryTotalCount,
      setAlert,
      setLoader
    );
  }, [mrn, setAlert, outreachHistoryFilters]);
  return (
    <MuiBox
      id="pep-my-studies-table-box"
      sx={{ width: "100%", overflow: "auto" }}
    >
      <MuiDataGrid
        slots={{
          loadingOverlay: LoadingGridOverlay,
          noRowsOverlay: NoStudyOverlay,
        }}
        rows={outreachHistory}
        getRowId={(row) => row.studyId}
        columns={defaultTableColumnHeaders(t)}
        autoHeight
        pagination
        disableColumnMenu
        disableColumnResize
        disableColumnReorder
        disableMultipleColumnsSorting
        disableRowSelectionOnClick
        loading={isLoading[LOADER_TYPES.PROSPECT_DETAILS_OUTREACH_HISTORY]}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={{
          page: page,
          pageSize: rowsPerPage,
        }}
        rowCount={outreachHistoryTotalCount}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={[{ field: sort.orderBy, sort: sort.order }]}
        sortingOrder={["asc", "desc"]}
        onSortModelChange={handleSortChange}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd"
        }
        sx={tableStyles}
        columnHeaderHeight={60}
        getRowHeight={() => "auto"}
      />
    </MuiBox>
  );
};

export default ProspectDetailsOutreachHistory;
